// useLevelOneComplete — a single source of truth for "has the user finished
// Level 1 yet?". We use this to gate the visibility of pricing surfaces
// (Pricing nav link, Join CTA, paywall mentions) so brand-new visitors are
// never confronted with plans before they've played the game once.
//
// Definition of "Level 1 complete": the user has earned at least one
// XP event (any quest, any quick win, any daily hack). That mirrors how
// the level system works — total_xp > 0 means they've taken one real
// action and crossed out of the absolute beginner state.
//
// We cache the result in localStorage so the UI has a synchronous answer
// on first render (avoids a flash of "Pricing" appearing then disappearing
// while useXP loads).
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useXP } from "@/hooks/useXP";
import { trackFunnel } from "@/lib/funnelTracker";

const CACHE_KEY = "hl_level1_complete";
// Separate marker so we only fire the analytics event the first time we
// observe the unlock — even across tabs / page reloads. The presence of
// this key means "we've already reported this milestone for this browser".
const TRACKED_KEY = "hl_level1_complete_tracked";

const readCache = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(CACHE_KEY) === "1";
  } catch {
    return false;
  }
};

const writeCache = (done: boolean) => {
  if (typeof window === "undefined") return;
  try {
    if (done) window.localStorage.setItem(CACHE_KEY, "1");
    else window.localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Returns true once the signed-in user has earned any XP. Guests always
 * read as `false`. Reads cached state synchronously, then refines from
 * the server.
 */
export const useLevelOneComplete = (): boolean => {
  const { user } = useAuth();
  const { xp } = useXP();
  const [cached, setCached] = useState<boolean>(() => readCache());

  useEffect(() => {
    // Reset the cache when signing out — guests must always see the
    // pre-Level-1 experience (no pricing nudges) regardless of what the
    // previous account had banked.
    if (!user) {
      writeCache(false);
      setCached(false);
      return;
    }
    if (xp && xp.total_xp > 0) {
      if (!cached) {
        writeCache(true);
        setCached(true);
        // First time we observe Level 1 unlock → log the conversion event.
        // Guarded by a separate localStorage marker so refreshing the page
        // can't double-count it. Fire-and-forget; tracker swallows errors.
        try {
          if (typeof window !== "undefined" &&
              window.localStorage.getItem(TRACKED_KEY) !== "1") {
            window.localStorage.setItem(TRACKED_KEY, "1");
            void trackFunnel("level_one_complete", {
              total_xp: xp.total_xp,
              level: xp.level,
              tier: xp.tier,
            });
          }
        } catch {
          /* localStorage unavailable — skip tracking, never break UX */
        }
      }
    }
  }, [user, xp, cached]);

  if (!user) return false;
  return cached || (xp ? xp.total_xp > 0 : false);
};
